import { action, autorun, computed, makeObservable, observable, when } from 'mobx';

import { SOCKET_MYSTERY_ROLLED } from '../../config/constants';
import { applyMysteryResults, mysteryIdsOf } from '../../features/mystery/applyMysteryResults';
import type { MysteryResult, QueuedReveal } from '../../features/mystery/types';
import { getRequest, postRequest } from '../../services/api';
import { socket } from '../../services/socket';
import type { ApiResponse } from '../../services/types';
import { isRollingOfferPopupType } from '../../utils/events';
import { sleep } from '../../utils/helpers';
import { devError } from '../../utils/log';
import type { RootStore } from '../RootStore';
import type { ClaimableReward } from '../ui/types';

// The push normally lands first; then a bounded poll, like the % Extra grant's fallback (EventTriggerStore).
export const MYSTERY_PUSH_WAIT_MS = 3000;
export const MYSTERY_POLL_ATTEMPTS = 4;
export const MYSTERY_POLL_DELAY_MS = 1500;

const byRevealOrder = (a: MysteryResult, b: MysteryResult) =>
  (a.packageOrder ?? 0) - (b.packageOrder ?? 0) || a.createdAt.localeCompare(b.createdAt);

/**
 * Mystery results for this player: received by push (`MYSTERY_ROLLED`), the gift claim response or the
 * `unrevealed` call; played once each (by rollId), then acked so they aren't replayed.
 */
export class MysteryStore {
  /** Received, not played yet, by rollId. */
  @observable.shallow private pending = new Map<string, MysteryResult>();

  /** What the reveal host shows now; the first one plays. */
  @observable.shallow revealQueue: QueuedReveal[] = [];

  /** Claims waiting for their results or playing their reveals. */
  @observable private resolving = 0;

  /** Mounted reveal hosts (MysteryRevealHost, in <Dialogs/>); a page without <Dialogs/> has none. */
  @observable private hosts = 0;

  /** Mounted MYSTERY_REWARD slots (an open offer or gift showing the item's Rive), by mysteryId. */
  @observable.shallow private slots = new Map<string, number>();

  /** From a claim's reveal in a slot until its popup has closed (or, left open, its claim animation has ended). */
  @observable private popupLocked = false;

  private played = new Set<string>();
  /** `false` when a sign-out cut the queue short: not acked, so the next session replays it. */
  private onQueueDone: ((completed: boolean) => void) | null = null;
  // Bumped on sign-out, so a call made before it neither keeps polling nor brings its answer into the next session.
  @observable private session = 0;

  constructor(private readonly rootStore: RootStore) {
    makeObservable(this);
    // Pushed as { data: { results, playerId } }, like CLAIM_PACKAGE_COMPLETED's envelope.
    const onPush = (message?: { data?: { results?: MysteryResult[] } }) => this.receive(message?.data?.results);

    autorun(() => {
      if (this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized) {
        socket.on(SOCKET_MYSTERY_ROLLED, onPush);
        void this.replayUnrevealed();
      } else {
        socket.off(SOCKET_MYSTERY_ROLLED, onPush);
        this.reset();
      }
    });
  }

  /** Pop-ups wait while true, as they do for the claim animation (the feature manager, a granted % Extra). */
  @computed get isRevealing() {
    return this.resolving > 0 || this.revealQueue.length > 0;
  }

  /**
   * An open offer or gift showing a Mystery slot hides its buy and claim buttons from its claim's reveal until the popup
   * has closed after the claim animation (MZ-867): the prize is already paid. A replay or another animation doesn't.
   */
  @computed get locksPopup() {
    return this.slots.size > 0 && (this.resolving > 0 || this.popupLocked);
  }

  @action receive(results: MysteryResult[] = []) {
    results
      .filter((result) => !this.played.has(result.rollId))
      .forEach((result) => this.pending.set(result.rollId, result));
  }

  @action private reset() {
    this.session += 1;
    this.popupLocked = false;
    this.pending.clear();
    this.played.clear();
    this.endQueue(false);
  }

  /**
   * The reveal host's mount effect; returns its unmount. Losing the last host (a page without <Dialogs/>, a crash)
   * ends what was queued unacked, so it never holds the claim or the pop-ups — the next session replays it.
   */
  @action registerHost = () => {
    this.hosts += 1;
    return action(() => {
      this.hosts -= 1;
      if (!this.hosts) this.endQueue(false);
    });
  };

  /**
   * A slot's mount effect, like registerHost; returns its unmount. A slot that leaves while its reveal plays hands it to
   * the overlay. The popup can't be closed during it (useHiddenByMysteryReveal, EventTriggerDialog).
   * Released a tick later, so StrictMode's remount keeps the slot.
   */
  @action registerSlot = (mysteryId: string) => {
    this.slots.set(mysteryId, (this.slots.get(mysteryId) ?? 0) + 1);
    return () => {
      setTimeout(
        action(() => {
          const left = (this.slots.get(mysteryId) ?? 1) - 1;
          if (left) {
            this.slots.set(mysteryId, left);
            return;
          }
          this.slots.delete(mysteryId);
        }),
      );
    };
  };

  /**
   * A claim's reveal plays in its item's slot when the offer or gift is on screen; otherwise (a replay, a closed popup)
   * the host's overlay plays it. Read while rendering, so a slot mounting or unmounting moves a reveal across.
   */
  inSlot(result: QueuedReveal) {
    return !result.displayOnly && this.slots.has(result.mysteryId);
  }

  /**
   * A purchase's success screen: its reveal starts only when the screen ends, so pop-ups (a granted % Extra) wait from
   * now. Returns the release, for the screen's unmount.
   */
  @action holdForReveal = () => {
    this.resolving += 1;
    return action(() => {
      this.resolving -= 1;
    });
  };

  /**
   * Rewards without a Mystery item go to `animate` at once, exactly as before Mystery; the others after their reveal.
   * Returns the rolled rewards' promise, only when there is a reveal.
   */
  afterReveal(
    rewards: ClaimableReward[],
    animate: (rewards: ClaimableReward[]) => void,
    options?: { answered?: boolean },
  ) {
    if (!mysteryIdsOf(rewards).length) {
      animate(rewards);
      return undefined;
    }
    const resolved = this.resolveRewards(rewards, options);
    void resolved.then(animate);
    return resolved;
  }

  /**
   * Plays the reveal for the Mystery items in these rewards, then returns them with the rolled prizes in place
   * (for the claim screen). Never throws and never blocks the reward: no result → the item is dropped.
   * `answered`: the claim response already carried the results (a gift) — one it lacks is looked up once, not waited for.
   */
  async resolveRewards(rewards: ClaimableReward[], { answered = false } = {}): Promise<ClaimableReward[]> {
    const mysteryIds = mysteryIdsOf(rewards);
    if (!mysteryIds.length) return rewards;

    this.setResolving(1);
    try {
      await this.waitForResults(mysteryIds, answered);
      const results = this.take(mysteryIds);
      await this.play(results);
      return applyMysteryResults(rewards, results);
    } catch (error) {
      devError('Mystery reveal failed', error);
      return applyMysteryResults(rewards, []);
    } finally {
      this.setResolving(-1);
    }
  }

  @action private setResolving(delta: 1 | -1) {
    // Ending: the claim animation blocks the screen from here, in the same step, so no pop-up opens in between.
    if (delta < 0) {
      this.rootStore.animationStore.setAnimationBlockScreenVisible(true);
      if (this.slots.size) this.lockPopupUntilClosed();
    }
    this.resolving += delta;
  }

  @action private lockPopupUntilClosed() {
    const { animationStore, eventTriggerStore } = this.rootStore;
    this.popupLocked = true;
    // The new claim animation drops the block screen before its onEnd closes the popup (and a mini game or the Challenges
    // dialog may come in between), so a popup that closes after its claim stays locked until it has. A rolling offer
    // stays open on its next step: it unlocks with the animation. A popup closed for any reason unlocks, even when its
    // claim animation never ends, so the next popup isn't left without its buttons.
    void when(() => {
      if (eventTriggerStore.isPopupCloseAnimationRunning) return false;
      if (!eventTriggerStore.isPopupOpen) return true;
      const popup = eventTriggerStore.currentActiveEventPopup;
      return !animationStore.animationBlockScreenVisible && (!popup || isRollingOfferPopupType(popup.benefit));
    }).then(
      action(() => {
        this.popupLocked = false;
      }),
    );
  }

  // By server time only: players set the device clock forward to skip timers.
  private resultsFor(mysteryIds: string[]): MysteryResult[] {
    const unplayed = [...this.pending.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return mysteryIds.flatMap((id) => {
      const index = unplayed.findIndex((result) => result.mysteryId === id);
      return index === -1 ? [] : unplayed.splice(index, 1);
    });
  }

  private hasAll(mysteryIds: string[]) {
    return this.resultsFor(mysteryIds).length === mysteryIds.length;
  }

  /** Picks the claim's results and takes them out in one step, so two claims never share a roll. */
  @action private take(mysteryIds: string[]): MysteryResult[] {
    const results = this.resultsFor(mysteryIds);
    results.forEach((result) => this.pending.delete(result.rollId));
    return results;
  }

  private async waitForResults(mysteryIds: string[], answered: boolean) {
    // Read before the wait: a sign-out during it must stop the poll too.
    const { session } = this;
    if (!answered) {
      await when(() => this.hasAll(mysteryIds), { timeout: MYSTERY_PUSH_WAIT_MS }).catch(() => undefined);
    }
    // An answered claim starts at the last attempt: one look.
    await this.pollUnrevealed(mysteryIds, answered ? MYSTERY_POLL_ATTEMPTS : 1, session);
  }

  // Bounded, recursive like EventTriggerStore.lookUpGrantedOffer.
  private async pollUnrevealed(mysteryIds: string[], attempt: number, session: number): Promise<void> {
    if (this.hasAll(mysteryIds) || session !== this.session) return;
    this.receive(await this.fetchUnrevealed());
    if (this.hasAll(mysteryIds) || attempt >= MYSTERY_POLL_ATTEMPTS) return;
    await sleep(MYSTERY_POLL_DELAY_MS);
    await this.pollUnrevealed(mysteryIds, attempt + 1, session);
  }

  /** The `unrevealed` call; nothing if it failed or the player signed out meanwhile. */
  private async fetchUnrevealed(): Promise<MysteryResult[]> {
    const { session } = this;
    try {
      const res = await getRequest<ApiResponse<MysteryResult[]>>('/v2/mystery/results/unrevealed');
      return session === this.session ? res.data.data : [];
    } catch (error) {
      devError('Mystery unrevealed fetch failed', error);
      return [];
    }
  }

  /**
   * App start / sign-in: rolls whose reveal never played (interrupted) — display only, the prize is granted. Only
   * what the call returned: a push that landed meanwhile belongs to a claim, which plays it itself.
   */
  private async replayUnrevealed() {
    await this.play(await this.fetchUnrevealed(), true);
  }

  /**
   * Shows the reveals one after another (Buy All in package order), then acks them in one call. A claim's reveal
   * ends at the Hide Item and the claim screen shows the prize; a replay (`displayOnly`) shows the prize itself.
   * A replay waits for a page that can show it; a claim with nothing to show it on goes straight on to its claim
   * screen, as with no Hide Item.
   */
  private async play(results: MysteryResult[], displayOnly = false): Promise<void> {
    const toPlay = results.filter((result) => !this.played.has(result.rollId)).sort(byRevealOrder);
    if (!toPlay.length) return;

    this.markPlayed(toPlay);
    const { session } = this;
    if (displayOnly) await when(() => this.hosts > 0 || session !== this.session);
    if (session !== this.session) return;

    const completed =
      !this.hosts || (await new Promise<boolean>((resolve) => this.enqueue(toPlay, displayOnly, resolve)));
    if (!completed) return;

    void postRequest('/v2/mystery/results/ack', { rollIds: toPlay.map((result) => result.rollId) }).catch((error) =>
      devError('Mystery ack failed', error),
    );
  }

  @action private markPlayed(results: MysteryResult[]) {
    results.forEach((result) => {
      this.played.add(result.rollId);
      this.pending.delete(result.rollId);
    });
  }

  @action private enqueue(toPlay: MysteryResult[], displayOnly: boolean, resolve: (completed: boolean) => void) {
    const previous = this.onQueueDone;
    this.onQueueDone = (completed) => {
      previous?.(completed);
      resolve(completed);
    };
    this.revealQueue = [...this.revealQueue, ...toPlay.map((result) => ({ ...result, displayOnly }))];
  }

  /** Called by the reveal host when a reveal finishes; a repeat call (StrictMode re-runs effects) is ignored. */
  @action next = (rollId: string) => {
    if (this.revealQueue[0]?.rollId !== rollId) return;
    this.revealQueue = this.revealQueue.slice(1);
    if (!this.revealQueue.length) this.endQueue(true);
  };

  @action private endQueue(completed: boolean) {
    this.revealQueue = [];
    const done = this.onQueueDone;
    this.onQueueDone = null;
    done?.(completed);
  }
}
